import React, { useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useBills, useIncomes, useCreateIncome, useDeleteIncome, useUpdateIncome, usePayments } from "../lib/hooks";
import { formatMoney, FREQUENCIES, monthRange, occurrencesInRange, todayIso, CURRENCIES, INCOME_CATEGORIES } from "../lib/finance";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, TrendingUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { usePaywall } from "../components/Paywall";

const empty = { name: "", amount: "", first_date: new Date().toISOString().slice(0, 10), frequency: "monthly", category: "Salary", notes: "" };

export default function IncomePage() {
  const { user } = useAuth();
  const currency = user?.prefs?.currency || "USD";
  const symbol = CURRENCIES[currency]?.symbol || "$";
  const { data: incomes = [] } = useIncomes();
  const { data: bills = [] } = useBills();
  const create = useCreateIncome();
  const update = useUpdateIncome();
  const del = useDeleteIncome();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const paywall = usePaywall();

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (i) => { setEditing(i.income_id); setForm({ ...i }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: Number(form.amount) };
    if (!payload.name || !payload.amount) { toast.error("Name and amount required"); return; }
    try {
      if (editing) { await update.mutateAsync({ id: editing, data: payload }); toast.success("Updated"); }
      else { await create.mutateAsync(payload); toast.success("Income added"); }
      setOpen(false);
    } catch (err) {
      if (err?.response?.status === 402) {
        setOpen(false);
        paywall.gateQuota("2 income sources max on Free tier");
        return;
      }
      toast.error("Save failed");
    }
  };

  const openNewGated = () => {
    if (!paywall.isPaid && incomes.length >= 2) {
      paywall.gateQuota("2 income sources max on Free tier");
      return;
    }
    openNew();
  };
  const remove = async (id) => {
    if (!confirm("Delete this income stream?")) return;
    await del.mutateAsync(id); toast.success("Deleted");
  };

  const now = new Date();
  const { start, end } = monthRange(now.getFullYear(), now.getMonth());

  const totals = useMemo(() => {
    let income = 0, bills_ = 0;
    for (const inc of incomes) {
      const occs = occurrencesInRange(inc.first_date, inc.frequency, start, end);
      income += occs.length * Number(inc.amount || 0);
    }
    for (const b of bills) {
      const occs = occurrencesInRange(b.first_due_date, b.frequency, start, end);
      bills_ += occs.length * Number(b.amount || 0);
    }
    return { income, bills: bills_, safe: income - bills_ };
  }, [incomes, bills, start, end]);

  const nextPayday = useMemo(() => {
    const t = new Date(todayIso());
    const horizon = new Date(t); horizon.setMonth(horizon.getMonth() + 2);
    const all = [];
    for (const inc of incomes) {
      for (const d of occurrencesInRange(inc.first_date, inc.frequency, t, horizon)) all.push({ inc, d });
    }
    all.sort((a, b) => a.d.localeCompare(b.d));
    return all[0];
  }, [incomes]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6" data-testid="income-page">
      {paywall.Modal}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Paydays & Income</h1>
          <p className="text-sm text-muted-foreground mt-1">Salary, freelance, side hustles — line them up.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNewGated} data-testid="add-income-btn"><Plus size={16} className="mr-1"/> Add income</Button>
          </DialogTrigger>
          <DialogContent data-testid="income-dialog">
            <DialogHeader><DialogTitle>{editing ? "Edit income" : "New income"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div><Label>Source name</Label>
                <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="income-form-name-input" required/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Amount ({symbol})</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount: e.target.value})} data-testid="income-form-amount-input" required/>
                </div>
                <div className="hidden">{/* legacy freq slot removed */}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Frequency</Label>
                  <Select value={form.frequency} onValueChange={(v)=>setForm({...form, frequency: v})}>
                    <SelectTrigger data-testid="income-form-frequency-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{FREQUENCIES.map((f)=><SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v)=>setForm({...form, category: v})}>
                    <SelectTrigger data-testid="income-form-category-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{INCOME_CATEGORIES.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>{form.category === "Starting Net" ? "As-of date" : "First payday"}</Label>
                <Input type="date" value={form.first_date} onChange={(e)=>setForm({...form, first_date: e.target.value})} data-testid="income-form-date-input" required/>
                {form.category === "Starting Net" && (
                  <p className="text-[11px] text-muted-foreground mt-1">Seeds your running net on this date. Use one-time frequency for a true opening balance.</p>
                )}
              </div>
              <Button type="submit" className="w-full" data-testid="income-form-submit">{editing ? "Save changes" : "Add income"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Neat calculation card */}
      <div className="rounded-2xl border border-border bg-card p-6 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-emerald-200/40 blur-3xl dark:bg-emerald-500/10 pointer-events-none"/>
        <div className="relative">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Neat calculation · this month</div>
          <div className="mt-4 flex flex-wrap items-baseline gap-3 font-mono">
            <span className="tabular text-2xl text-[hsl(var(--paid-text))] font-bold">{formatMoney(totals.income, currency)}</span>
            <span className="text-muted-foreground">−</span>
            <span className="tabular text-2xl text-[hsl(var(--due-text))] font-bold">{formatMoney(totals.bills, currency)}</span>
            <span className="text-muted-foreground">=</span>
            <span className={`tabular text-3xl font-black ${totals.safe >= 0 ? "text-[hsl(var(--paid-text))]" : "text-[hsl(var(--overdue-text))]"}`} data-testid="safe-to-spend">
              {formatMoney(totals.safe, currency)}
            </span>
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold ml-2">Safe to spend</span>
          </div>
          {nextPayday && (
            <div className="mt-4 text-sm text-muted-foreground flex items-center gap-2">
              <TrendingUp size={14}/> Next payday: <span className="font-semibold text-foreground">{nextPayday.inc.name}</span> on <span className="tabular">{new Date(nextPayday.d).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {incomes.length === 0 && (
          <div className="col-span-full text-center text-sm text-muted-foreground py-16 border border-dashed rounded-xl" data-testid="incomes-empty">
            No income sources yet. Add your salary to start.
          </div>
        )}
        {incomes.map((i) => (
          <Card key={i.income_id} data-testid={`income-card-${i.income_id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{i.category || "Income"} · {FREQUENCIES.find((f)=>f.value===i.frequency)?.label}</div>
                  <div className="font-display text-xl font-bold mt-1">{i.name}</div>
                </div>
                <div className="tabular font-bold text-[hsl(var(--paid-text))]">{formatMoney(i.amount, currency)}</div>
              </div>
              <div className="mt-3 text-sm text-muted-foreground">Starts {new Date(i.first_date).toLocaleDateString()}</div>
              <div className="mt-4 flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={()=>openEdit(i)} data-testid={`income-edit-${i.income_id}`}><Pencil size={12}/></Button>
                <Button size="sm" variant="outline" onClick={()=>remove(i.income_id)} data-testid={`income-delete-${i.income_id}`}><Trash2 size={12}/></Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
